// Le REJEU d'un graphique Chart.js de l'écran sur une autre toile — commun au SVG (30/09) et
// au PNG (#520, 05/10).
//
// Le graphique à l'écran est retrouvé par son canvas (`Chart.getChart`), puis rejoué à
// l'identique : même type, mêmes données, mêmes options, mêmes greffons (les étiquettes de
// valeur de `ChartBlock` comprises), même taille en px CSS, et les séries masquées par la
// légende restent masquées. Seule la DENSITÉ change : 1 pour le SVG (des coordonnées en px
// CSS), au moins 2 pour le PNG — un canvas d'écran ordinaire (densité 1) recopié puis agrandi
// donnait une courbe floue dans l'image de partage.
//
// Le rejeu tourne sur la `BasicPlatform` de Chart.js : pas d'événement, pas de
// redimensionnement, pas d'animation — un rendu unique et synchrone, puis on détruit.
import { BasicPlatform, Chart } from 'chart.js'
import type { ChartConfiguration, ChartData } from 'chart.js'

/** Les données, copiées : Chart.js greffe des écouteurs sur les tableaux qu'il reçoit, et le
 *  graphique de l'écran ne doit rien partager avec son double éphémère. */
function copierDonnees(data: ChartData): ChartData {
  return {
    ...data,
    labels: data.labels ? [...data.labels] : data.labels,
    datasets: data.datasets.map(d => ({ ...d, data: Array.isArray(d.data) ? [...d.data] : d.data })),
  } as ChartData
}

// ── Repli des comparaisons (Olivier, 14/10) ──────────────────────────────────────────────
// Une tuile repliée ne trace à l'écran que le territoire consulté ; son image partagée (PNG et
// SVG) garde les comparaisons, comme l'étude PDF et le CSV — le repli est un confort de lecture,
// pas un filtre des exports. `ChartBlock` dépose donc, pour SA toile et tant qu'elle est repliée,
// de quoi rejouer le graphique complet : sa configuration (les séries masquées par la légende y
// portent `hidden`) et sa hauteur (un empilé horizontal grandit d'une barre par territoire). La
// largeur reste celle de l'écran. Rien de déposé : le rejeu reprend l'écran, comme avant.
export interface GraphiqueComplet { config: ChartConfiguration; hauteur?: number }
const complets = new WeakMap<HTMLCanvasElement, () => GraphiqueComplet>()

/** Dépose (ou retire, `null`) le graphique complet à exporter à la place de celui de `toile`. */
export function deposerGraphiqueComplet(toile: HTMLCanvasElement,
                                        fournir: (() => GraphiqueComplet) | null): void {
  if (fournir) complets.set(toile, fournir)
  else complets.delete(toile)
}

/** Le graphique complet déposé pour `toile`, ou `null`. */
export function lireGraphiqueComplet(toile: HTMLCanvasElement): GraphiqueComplet | null {
  return complets.get(toile)?.() ?? null
}

/** Le graphique vivant derrière un canvas de l'écran, et sa taille en px CSS — celle du graphique
 *  complet quand la tuile est repliée (`complet`, à rejouer à la place de l'écran). */
export function graphiqueVivant(toile: HTMLCanvasElement):
  { vivant: Chart; largeur: number; hauteur: number; complet: GraphiqueComplet | null } {
  const vivant = Chart.getChart(toile)
  if (!vivant || !vivant.ctx) throw new Error('Le graphique n’est pas encore dessiné.')
  const largeur = Math.floor(vivant.width)
  const hauteurEcran = Math.floor(vivant.height)
  // En vue « Tableau », le canvas est masqué et Chart.js l'a réduit à rien : il n'y a pas
  // de graphique à exporter, on le dit plutôt que de livrer une image vide.
  if (!largeur || !hauteurEcran) {
    throw new Error('Le graphique n’est pas affiché — repassez en vue « Graphique ».')
  }
  const complet = lireGraphiqueComplet(toile)
  const hauteur = complet?.hauteur ? Math.floor(complet.hauteur) : hauteurEcran
  return { vivant, largeur, hauteur, complet }
}

/**
 * Rejoue `vivant` sur `cible` (dont `width` × `height` valent la taille CSS du graphique) à la
 * densité demandée, et rend ce que `lire` en tire. `lire` est appelé AVANT `destroy()`, qui
 * efface la toile : c'est là qu'on recopie les pixels ou qu'on sérialise le SVG.
 */
export function rejouer<T>(vivant: Chart, cible: HTMLCanvasElement, densite: number, lire: () => T,
                          complet: GraphiqueComplet | null = null): T {
  const config = complet?.config ?? vivant.config as ChartConfiguration
  const double = new Chart(cible, {
    type: config.type,
    data: copierDonnees(config.data),
    options: {
      ...config.options,
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
      devicePixelRatio: densite,
      events: [],
    },
    plugins: config.plugins,
    platform: BasicPlatform,
  } as ChartConfiguration)
  try {
    // Le graphique complet d'une tuile repliée porte lui-même ses séries masquées (`hidden`) :
    // l'écran, qui n'en trace qu'une partie, n'a rien à lui apprendre.
    if (complet) return lire()
    // Ce que l'écran montre : une série décochée dans la légende, une part masquée d'un
    // anneau, restent absentes de l'export.
    let change = false
    config.data.datasets.forEach((_, i) => {
      const visible = vivant.isDatasetVisible(i)
      if (double.isDatasetVisible(i) !== visible) { double.setDatasetVisibility(i, visible); change = true }
    })
    const nbPoints = config.data.labels?.length ?? 0
    for (let i = 0; i < nbPoints; i++) {
      if (!vivant.getDataVisibility(i)) { double.toggleDataVisibility(i); change = true }
    }
    if (change) double.update()
    return lire()
  } finally {
    double.destroy()
  }
}
